import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownMenu } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import {
  ProductBundle,
  ServiceItemType
} from "@/pages/settings/pages/products-v2/types";
import { cn } from "@/utils/utils";

const TRANSLATION_KEY = "contractTerms.bundle";

interface BundleMenuListProps {
  bundles: ProductBundle[];
  search: string;
  serviceItemTypes: ServiceItemType[];
  onSelect: (bundle: ProductBundle) => void;
}

// The Bundles view of Add service: a row adds its bundle, its chevron previews
// the bundle's services.
export const BundleMenuList: React.FC<BundleMenuListProps> = ({
  bundles,
  search,
  serviceItemTypes,
  onSelect
}) => {
  const { t } = useTranslation();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const normalizedSearch = search.trim().toLowerCase();
  const filtered = bundles.filter(({ name }) =>
    name.toLowerCase().includes(normalizedSearch)
  );

  const toggle = (bundleId: string) =>
    setExpandedIds((ids) => {
      const next = new Set(ids);
      if (next.has(bundleId)) next.delete(bundleId);
      else next.add(bundleId);
      return next;
    });

  const typeName = (typeId: number | null) =>
    serviceItemTypes.find(({ id }) => id === typeId)?.name ?? "";

  if (filtered.length === 0) {
    return (
      <Text variant="bodyTertiary" className="px-2 py-4 text-center">
        {t("tables.emptyState.title")}
      </Text>
    );
  }

  return (
    <div className="flex flex-col" data-test="add-service-bundles">
      {filtered.map((bundle) => {
        const isExpanded = expandedIds.has(bundle.id);
        return (
          <div key={bundle.id} className="flex flex-col">
            <div className="flex items-center gap-1 rounded-lg pl-1 hover:bg-surface-hover-soft active:bg-surface-pressed">
              <button
                type="button"
                onClick={() => toggle(bundle.id)}
                aria-expanded={isExpanded}
                aria-label={t(`${TRANSLATION_KEY}.preview`, {
                  name: bundle.name
                })}
                className="flex shrink-0 rounded-sm p-0.5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                data-test={`add-service-bundle-toggle-${bundle.id}`}
              >
                <IconContainer
                  as="span"
                  size="sm"
                  color="primary"
                  className={cn(!isExpanded && "-rotate-90")}
                  aria-hidden="true"
                >
                  <ChevronDownMenu />
                </IconContainer>
              </button>
              <button
                type="button"
                onClick={() => onSelect(bundle)}
                className="flex min-w-0 flex-1 items-center justify-between gap-4 py-2 pr-3 text-left"
                data-test={`add-service-bundle-${bundle.id}`}
              >
                <Text variant="body" className="truncate">
                  {bundle.name}
                </Text>
                <Text variant="smBodyTertiary" className="shrink-0">
                  {t(`${TRANSLATION_KEY}.serviceCount`, {
                    count: bundle.items.length
                  })}
                </Text>
              </button>
            </div>
            {isExpanded &&
              bundle.items.map((item) => (
                <div
                  key={item.serviceItemId}
                  className="flex items-center gap-2 pb-0.5 pl-9 pr-3 pt-2"
                  data-test={`add-service-bundle-item-${item.serviceItemId}`}
                >
                  <Text variant="smBodySecondary" className="flex-1 truncate">
                    {t(`${TRANSLATION_KEY}.itemLine`, {
                      quantity: item.quantity.toLocaleString(),
                      name: item.serviceItemName ?? ""
                    })}
                  </Text>
                  <Text variant="smBodyTertiary" className="shrink-0">
                    {typeName(item.serviceItemType)}
                  </Text>
                </div>
              ))}
          </div>
        );
      })}
    </div>
  );
};
